import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { updateCurrentModelLocally } from '../store/modelSlice';
import { etapesAmbContingut } from '../services/mapesSSM';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon,
         QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import AjudaEtapa from './ssm/AjudaEtapa';
import {
  DocumentTextIcon,
  PencilSquareIcon,
  LightBulbIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ArrowPathIcon,
  RocketLaunchIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

// Stage components
import ProblemSituationStage from './ssm/ProblemSituationStage';
import RichPictureStage from './ssm/RichPictureStage';
import RootDefinitionsStage from './ssm/RootDefinitionsStage';
import ConceptualModelsStage from './ssm/ConceptualModelsStage';
import ComparisonStage from './ssm/ComparisonStage';
import ChangesStage from './ssm/ChangesStage';
import ImplementationStage from './ssm/ImplementationStage';

const SSMInterface = ({ modelId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentModel } = useSelector(state => state.models);
  // Quina etapa es mira ve del model i s'hi desa: reobrir-lo et torna on eres. En una sessió de
  // classe partida en dos dies, tornar sempre a l'etapa 1 és una molèstia petita i diària.
  const currentStage = currentModel?.softSystemsModel?.stage || 'problem_situation';
  const setCurrentStage = (stage) => {
    dispatch(updateCurrentModelLocally({
      softSystemsModel: { ...(currentModel?.softSystemsModel || {}), stage },
    }));
  };

  const stages = [
    {
      id: 'problem_situation',
      name: t('ssm.s1'),
      description: t('ssm.s1d'),
      icon: DocumentTextIcon,
      color: 'text-primary-500',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'problem_expressed',
      name: t('ssm.s2'),
      description: t('ssm.s2d'),
      icon: PencilSquareIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'root_definitions',
      name: t('ssm.s3'),
      description: t('ssm.s3d'),
      icon: LightBulbIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'conceptual_models',
      name: t('ssm.s4'),
      description: t('ssm.s4d'),
      icon: Cog6ToothIcon,
      color: 'text-primary-700',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'comparison',
      name: t('ssm.s5'),
      description: t('ssm.s5d'),
      icon: ChartBarIcon,
      color: 'text-primary-700',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'changes',
      name: t('ssm.s6'),
      description: t('ssm.s6d'),
      icon: ArrowPathIcon,
      color: 'text-primary-800',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    },
    {
      id: 'implementation',
      name: t('ssm.s7'),
      description: t('ssm.s7d'),
      icon: RocketLaunchIcon,
      color: 'text-primary-900',
      bgColor: 'bg-primary-50',
      borderColor: 'border-primary-200'
    }
  ];

  // LA BASTIDA BUIDA LA DONA EL SERVIDOR, no aquest efecte.
  //
  // Fins al 26/08/2026 aquí s'hi escrivia un esquelet de set etapes quan `softSystemsModel` no
  // hi era. Amb la càrrega des de l'API, això és **una cursa**: si l'efecte guanya, escriu la
  // bastida buida damunt del que s'acaba de demanar i les pantalles surten en blanc, **sense cap
  // error i amb cara de dades perdudes**. És la mateixa família que l'`unwrap()` que el
  // 25/08/2026 feia que l'editor no demanés mai el dibuix desat.
  //
  // El `GET` retorna sempre les set etapes, buides o plenes, o sigui que no cal cap bastida aquí.

  const updateSSMData = (stageData) => {
    dispatch(updateCurrentModelLocally({
      softSystemsModel: {
        ...currentModel.softSystemsModel,
        ...stageData
      }
    }));
  };

  const getStageComponent = (stageId) => {
    const props = {
      data: currentModel?.softSystemsModel || {},
      onUpdate: updateSSMData,
      onNext: () => {
        const currentIndex = stages.findIndex(s => s.id === stageId);
        if (currentIndex < stages.length - 1) {
          setCurrentStage(stages[currentIndex + 1].id);
        }
      }
    };

    switch (stageId) {
      case 'problem_situation':
        return <ProblemSituationStage {...props} />;
      case 'problem_expressed':
        return <RichPictureStage {...props} />;
      case 'root_definitions':
        return <RootDefinitionsStage {...props} />;
      case 'conceptual_models':
        return <ConceptualModelsStage {...props} />;
      case 'comparison':
        return <ComparisonStage {...props} />;
      case 'changes':
        return <ChangesStage {...props} />;
      case 'implementation':
        return <ImplementationStage {...props} />;
      default:
        return <ProblemSituationStage {...props} />;
    }
  };

  const getCurrentStageIndex = () => stages.findIndex(s => s.id === currentStage);
  const fetes = etapesAmbContingut(currentModel?.softSystemsModel);

  // LA LLISTA D'ETAPES ES PLEGA.
  //
  // Són 320 px que **mentre dibuixes no fas servir**, i a les etapes 2 i 4 —que porten llenç— la
  // diferència entre tenir-los i no tenir-los és si el llenç és utilitzable. Plegada queda una
  // franja amb els set números i la marca de contingut: es veu on ets i s'hi pot saltar igual.
  const [plegada, setPlegada] = useState(false);
  const [ajudaDe, setAjudaDe] = useState(null);

  // LA LÍNIA, que és la idea estructural més important del diagrama de Checkland i que fins avui
  // no es veia enlloc.
  //
  //     1. L'embolic            ┐
  //     2. Imatge rica          ┘  EL MÓN REAL — el que mires, discuteixes i fas
  //     ─────────────────────────
  //     3. Definicions arrel    ┐  EL PENSAMENT DE SISTEMES — construccions nocionals,
  //     4. Models conceptuals   ┘  no descripcions del món
  //     ─────────────────────────
  //     5. Comparació           ┐
  //     6. Canvis               │  EL MÓN REAL
  //     7. Acció                ┘
  //
  // **Les etapes 3 i 4 són SOTA la línia**, i que ho siguin és el que impedeix l'errada que
  // tothom fa: confondre el model conceptual amb com haurien de funcionar les coses. Una
  // definició arrel no descriu el món; diu com el mires. I la comparació de l'etapa 5 només té
  // sentit **perquè les dues bandes són coses diferents**.
  //
  // Es dibuixa **sense trencar la numeració**: la línia es creua dues vegades —abans de la 3 i
  // després de la 4—, que és com Checkland ho dibuixa. Partir-ho en dos grups sí que trencaria la
  // seqüència, i llavors ja no es veuria que s'hi baixa i se'n torna a pujar.
  const SOTA_LA_LINIA = ['root_definitions', 'conceptual_models'];
  const sotaLaLinia = (id) => SOTA_LA_LINIA.includes(id);

  return (
    <div className="flex h-full min-h-0">
      {/* Stage Navigation Sidebar */}
      <div className={`${plegada ? 'w-16' : 'w-80'} flex-shrink-0 bg-white border-r border-gray-200
                       flex flex-col min-h-0 transition-all duration-200`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-2">
            {!plegada && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('ssm.title')}</h2>
                <p className="text-sm text-gray-600 mt-1">{t('ssm.subtitle')}</p>
              </div>
            )}
            <button type="button" onClick={() => setPlegada(!plegada)}
              title={t(plegada ? 'ssm.desplega' : 'ssm.plega')}
              aria-label={t(plegada ? 'ssm.desplega' : 'ssm.plega')}
              className={`${plegada ? 'mx-auto' : ''} text-gray-400 hover:text-gray-600 p-1
                          rounded-md hover:bg-gray-100 flex-shrink-0`}>
              {plegada ? <ChevronDoubleRightIcon className="h-4 w-4" />
                       : <ChevronDoubleLeftIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className={`flex-1 min-h-0 overflow-y-auto ${plegada ? 'px-1.5 py-3' : 'p-4'}`}>
          {/* La primera banda. Les altres dues surten als creuaments de la línia. */}
          {!plegada && (
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
              {t('ssm.realWorld')}
            </p>
          )}
          <div className="space-y-3">
            {stages.map((stage, index) => {
              const Icon = stage.icon;
              const isActive = stage.id === currentStage;
              const isCompleted = fetes.has(stage.id);
              const sota = sotaLaLinia(stage.id);
              // On es creua la línia: just abans de la 3 i just després de la 4.
              const creuaAbans = sota && !sotaLaLinia(stages[index - 1]?.id);
              const creuaDespres = sota && !sotaLaLinia(stages[index + 1]?.id);

              return (
                <React.Fragment key={stage.id}>
                {creuaAbans && plegada && <div className="border-t-2 border-dashed border-gray-300 my-1" />}
                {creuaAbans && !plegada && (
                  <div className="pt-1">
                    <div className="border-t-2 border-dashed border-gray-300" />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-1.5">
                      {t('ssm.systemsThinking')}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-snug mb-1">
                      {t('ssm.systemsThinkingWhy')}
                    </p>
                  </div>
                )}
                <div
                  onClick={() => setCurrentStage(stage.id)}
                  title={plegada ? `${index + 1}. ${stage.name}` : undefined}
                  className={`${sota && !plegada ? 'ml-2 ' : ''}
                    relative ${plegada ? 'px-1 py-2 text-center' : 'p-4'} rounded-md border-2 cursor-pointer transition-all duration-200
                    ${isActive 
                      ? `${stage.bgColor} ${stage.borderColor} shadow-md` 
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }
                  `}
                >
                  <div className={plegada ? '' : 'flex items-start space-x-3'}>
                    <div className={`
                      flex-shrink-0 ${plegada ? 'p-0.5 mx-auto w-fit' : 'p-2'} rounded-md
                      ${isActive ? 'bg-white shadow-sm' : 'bg-gray-100'}
                    `}>
                      <Icon className={`${plegada ? 'h-4 w-4' : 'h-5 w-5'}
                        ${isActive ? stage.color : 'text-gray-500'}`} />
                    </div>
                    {plegada && (
                      <span className={`block text-[10px] font-semibold mt-1 ${
                        isCompleted ? 'text-ok-600' : 'text-gray-500'}`}>{index + 1}</span>
                    )}
                    
                    {!plegada && (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-sm font-medium ${
                          isActive ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {index + 1}. {stage.name}
                        </h3>
                        {/* La interrogació. **Només desplegada**: plegada no hi ha ni nom ni
                            espai, i un botó que amb prou feines s'encerta és pitjor que cap. */}
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setAjudaDe(stage); }}
                          title={t('ssm.ajTitol')} aria-label={`${t('ssm.ajTitol')}: ${stage.name}`}
                          className="ml-auto mr-1 text-gray-400 hover:text-primary-700 flex-shrink-0">
                          <QuestionMarkCircleIcon className="h-4 w-4" />
                        </button>
                        {isCompleted && (
                          <CheckCircleIcon className="h-4 w-4 text-ok-500" />
                        )}
                      </div>
                      <p className={`text-xs mt-1 ${
                        isActive ? 'text-gray-600' : 'text-gray-500'
                      }`}>
                        {stage.description}
                      </p>
                    </div>
                    )}
                  </div>

                  {/* La franja de l'esquerra diu si l'etapa té CONTINGUT, no per quina vas. */}
                  <div className={`absolute left-0 top-0 bottom-0 ${plegada ? 'w-0.5' : 'w-1'}
                                   bg-gray-200 rounded-l-md`}>
                    {(isActive || isCompleted) && (
                      <div className={`w-full h-full rounded-l-md transition-all duration-500
                        ${isCompleted ? 'bg-ok-500' : stage.color.replace('text-', 'bg-')}`} />
                    )}
                  </div>
                </div>
                {creuaDespres && plegada && <div className="border-t-2 border-dashed border-gray-300 my-1" />}
                {creuaDespres && !plegada && (
                  <div className="pt-1">
                    <div className="border-t-2 border-dashed border-gray-300" />
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-1.5">
                      {t('ssm.realWorld')}
                    </p>
                    <p className="text-[11px] text-gray-500 leading-snug mb-1">
                      {t('ssm.realWorldWhy')}
                    </p>
                  </div>
                )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* EL RECOMPTE ÉS D'ETAPES AMB CONTINGUT, no de per quina vas.
            Abans era «4 / 7» amb una barra que s'omplia segons on eres, i marcava com a fetes
            totes les anteriors. Al SSM es va endavant i endarrere a posta —tornar a la imatge
            rica després de mirar-se una definició arrel és la feina, no una marxa enrere— i una
            barra de cascada ensenya exactament el contrari del que s'ha d'aprendre. */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          {plegada ? (
            <p className="text-center text-xs font-medium text-gray-700 tabular-nums"
               title={t('ssm.ambContingut')}>{fetes.size}/{stages.length}</p>
          ) : (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('ssm.ambContingut')}</span>
              <span className="font-medium text-gray-900">{fetes.size} / {stages.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        {/* Stage Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {t('ssm.stageN', { n: getCurrentStageIndex() + 1 })}: {stages[getCurrentStageIndex()]?.name}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {stages[getCurrentStageIndex()]?.description}
              </p>
              {/* De quina banda de la línia ets. A les etapes 3 i 4 no és cap detall: és el que
                  impedeix confondre un model conceptual amb com haurien de ser les coses. */}
              <p className="text-xs text-gray-500 mt-1.5 max-w-3xl">
                <span className="font-medium">
                  {t(sotaLaLinia(currentStage) ? 'ssm.systemsThinking' : 'ssm.realWorld')}
                </span>
                {' — '}
                {t(sotaLaLinia(currentStage) ? 'ssm.systemsThinkingWhy' : 'ssm.realWorldWhy')}
              </p>
            </div>
            
            <div className="flex space-x-2">
              {getCurrentStageIndex() > 0 && (
                <button
                  onClick={() => setCurrentStage(stages[getCurrentStageIndex() - 1].id)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('ssm.previous')}
                </button>
              )}
              {getCurrentStageIndex() < stages.length - 1 && (
                <button
                  onClick={() => setCurrentStage(stages[getCurrentStageIndex() + 1].id)}
                  className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  {t('ssm.next')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stage Content */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {getStageComponent(currentStage)}
        </div>
      </div>

      {ajudaDe && (
        <AjudaEtapa etapa={ajudaDe} etapes={stages} onTancar={() => setAjudaDe(null)} />
      )}
    </div>
  );
};

export default SSMInterface;